import React, {
    createContext,
    Dispatch,
    FunctionComponent,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';

import { format } from 'date-fns';
import { toNumber } from 'lodash';
import { Button, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { useCurrentContext } from 'auth/Auth';
import { MessageActionType, useMessage } from 'common/message-bar/MessageBar';
import { OverlayDisplay } from 'core/overlay/OverlayDisplay';

import { getSingleTicket } from '../actions-api';

export enum TicketReloadModalActionType {
    Show,
    Hide,
}

export interface TicketReloadModal {
    visible: boolean;
    ticketId: string;
}

export interface TicketReloadModalAction {
    type: TicketReloadModalActionType;
    ticketId: string;
}

interface TicketReloadModalProviderProps {
    children: ReactNode;
}

const TicketReloadModalStateContext = createContext<TicketReloadModal | undefined>(undefined);
const TicketReloadDispatchContext = createContext<Dispatch<TicketReloadModalAction> | undefined>(undefined);

const ticketReloadModalReducer = (_: TicketReloadModal, action: TicketReloadModalAction): TicketReloadModal => {
    switch (action.type) {
        case TicketReloadModalActionType.Show: {
            return { visible: true, ticketId: action.ticketId };
        }

        case TicketReloadModalActionType.Hide: {
            return { visible: false, ticketId: '' };
        }

        default: {
            throw new Error(`Invalid action type: ${action.type}`);
        }
    }
};

export const TicketReloadModalProvider: FunctionComponent<TicketReloadModalProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(ticketReloadModalReducer, { visible: false, ticketId: '' });

    return (
        <TicketReloadModalStateContext.Provider value={state}>
            <TicketReloadDispatchContext.Provider value={dispatch}>
                {children}
                <TicketReload />
            </TicketReloadDispatchContext.Provider>
        </TicketReloadModalStateContext.Provider>
    );
};

export const useTicketReloadModalState = (): TicketReloadModal => {
    const context = useContext(TicketReloadModalStateContext);

    if (context === undefined) {
        throw new Error('useTicketReloadModalState must be used within a TicketReloadModalProvider');
    }

    return context;
};

export const useTicketReloadModalDispatch = (): Dispatch<TicketReloadModalAction> => {
    const context = useContext(TicketReloadDispatchContext);

    if (context === undefined) {
        throw new Error('useTicketReloadModalDispatch must be used within a TicketReloadModalProvider');
    }

    return context;
};

export const useTicketReloadModal = (): [TicketReloadModal, Dispatch<TicketReloadModalAction>] => {
    return [useTicketReloadModalState(), useTicketReloadModalDispatch()];
};

export const TicketReload: React.FC = React.memo(() => {
    const [message, setMessage] = useState<string>('');
    const [messageState, dispatch] = useTicketReloadModal();
    const { tenantId } = useCurrentContext();
    const { t } = useTranslation('tickets');
    const [, dispatchMessage] = useMessage();

    useEffect(() => {
        if (messageState.ticketId) {
            getSingleTicket(tenantId, toNumber(messageState.ticketId))
                .then((res) => {
                    const formattedDate = format(new Date(res.updatedDate), 'MMM dd, yyyy h:mm a');
                    const updatedBy = res.updatedByUser ? res.updatedByUser.displayName : t('SYSTEM_USER');
                    const tmpMessage = `This ticket has been changed by ${updatedBy} on ${formattedDate}. Please reload and try again.`;

                    setMessage(tmpMessage);
                })
                .catch(() => {
                    dispatchMessage({
                        type: MessageActionType.ShowError,
                        text: t('FAILED_TO_FETCH_TICKET'),
                    });
                });
        }
    }, [tenantId, messageState.ticketId, t, dispatchMessage]);

    const hide = useCallback((): void => {
        dispatch({ type: TicketReloadModalActionType.Hide, ticketId: '' });
    }, [dispatch]);

    if (!messageState.visible) {
        return null;
    }

    return (
        <>
            {message === '' && <OverlayDisplay />}
            <Modal
                className="custom-bg-ticket-reload-modal"
                show={!!messageState.visible && message !== ''}
                onHide={() => hide()}
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('TICKET_UPDATED')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="content">{message}</Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={() => {
                            window.location.reload();
                            hide();
                        }}
                    >
                        {t('RELOAD')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
});
